/** Settings → Notifications: push on this device (subscribe only from a tap), a test send, and the channel per kind. */
import { NOTIFICATION_KIND_LABELS } from "@contentops/contract";
import { useEffect, useState } from "react";
import type { NotificationSettings } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Button, Card, QueryView, Switch } from "../../components/ui";
import { useApi, useCache, useMutation } from "../../data/hooks";
import { useNotificationSettings } from "../../data/queries";
import { currentSubscription, disablePush, enablePush, pushSupport } from "../../pwa/push";

const CHANNELS = [{ value: "push", label: "Push + in app" }, { value: "in_app", label: "In app only" }, { value: "off", label: "Off" }] as const;
const label = (k: string) => NOTIFICATION_KIND_LABELS[k] ?? k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export function NotificationSettingsCard() {
  const q = useNotificationSettings();
  return (
    <Card title="Notifications" id="notifications">
      <PushDevice />
      <QueryView q={q}>{(s) => <Kinds s={s} />}</QueryView>
    </Card>
  );
}

function PushDevice() {
  const api = useApi();
  const toast = useToast();
  const support = pushSupport();
  const [state, setState] = useState<{ subscribed: boolean; permission: string } | null>(null);
  const [lostNotice, setLostNotice] = useState(false);
  const refresh = async () => {
    const sub = await currentSubscription().catch(() => null);
    const permission = "Notification" in window ? Notification.permission : "default";
    setState({ subscribed: !!sub, permission });
    // Permission granted but no subscription: the browser or the server (410 Gone) dropped this device's subscription.
    setLostNotice(permission === "granted" && !sub);
  };
  useEffect(() => { if (support === "supported") void refresh(); }, [support]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = useMutation(async (on: boolean) => { if (on) await enablePush(api); else await disablePush(api); }, {
    onSuccess: () => { void refresh(); toast.ok("Saved."); },
    onError: (e) => { void refresh(); toast.error(e, "Couldn't change push notifications"); },
  });
  const test = useMutation(() => api.pushTest(), {
    onSuccess: (r) => {
      if (r.disabled > 0) setLostNotice(true);
      toast.show({ tone: r.sent ? "ok" : "info", text: r.sent ? `Test sent to ${r.sent} device${r.sent === 1 ? "" : "s"}.` : "No device is subscribed yet." });
    },
    onError: (e) => toast.error(e, "Couldn't send a test"),
  });
  if (support === "ios_needs_home_screen") {
    return (
      <p className="note" role="note"><Icon name="share" size={18} /> On iPhone and iPad, notifications work when ContentOps is on your Home Screen: tap <b>Share → Add to Home Screen</b>, then open ContentOps from the icon (iOS 16.4 or later).</p>
    );
  }
  if (support === "unsupported") return <p className="muted small">This browser can't receive push notifications. You'll still see everything in the Notifications list.</p>;
  const on = !!state?.subscribed && state.permission === "granted";
  return (
    <div className="stack">
      <Switch label="Push notifications on this device" checked={on} disabled={toggle.pending || state === null} onChange={(v) => void toggle.mutate(v)}
        hint={state?.permission === "denied" ? "Blocked in this browser's site settings — allow notifications for ContentOps there, then turn this on." : "Failures, reviews ready and anything that needs you, even when ContentOps is closed."} />
      {lostNotice && <p className="audit-alert small" role="status"><Icon name="alert" size={18} /> This device's subscription was disabled (it expired or the browser dropped it). Turn push on again to keep getting notifications here.</p>}
      <div className="row"><Button size="sm" variant="secondary" icon="bell" busy={test.pending} onClick={() => void test.mutate()}>Send test notification</Button></div>
    </div>
  );
}

function Kinds({ s }: { s: NotificationSettings }) {
  const api = useApi();
  const cache = useCache();
  const toast = useToast();
  const save = useMutation((patch: NotificationSettings) => api.updateNotificationSettings(patch), {
    onSuccess: (r) => { cache.setData("settings/notifications", r); toast.ok("Saved."); },
    onError: (e) => { cache.invalidate("settings/notifications"); toast.error(e, "Couldn't save"); },
  });
  const kinds = Object.keys(s.kinds).sort((a, b) => label(a).localeCompare(label(b)));
  return (
    <div className="stack">
      <h3 className="autonomy-title">What to tell you about</h3>
      <ul className="notif-kinds">
        {kinds.map((k) => (
          <li key={k}>
            <label htmlFor={`nk-${k}`}>{label(k)}</label>
            <select id={`nk-${k}`} value={s.kinds[k]} disabled={save.pending} onChange={(e) => {
              const v = e.target.value as NotificationSettings["kinds"][string];
              cache.setData("settings/notifications", { kinds: { ...s.kinds, [k]: v } });
              void save.mutate({ kinds: { [k]: v } });
            }}>
              {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </li>
        ))}
      </ul>
    </div>
  );
}
